import {put, call} from 'redux-saga/effects';
import Referral from "../../entities/referral";
import * as Actions from "../actions/referralActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ReferralAction = {
    referralId: string;
    referral: Referral;
    referrals: Referral[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneReferral(action: ReferralAction) {
	const session = tabSession.captureGeneration();
	try {
		const referral: Referral = yield call(api.findOne, 'referral', action.referralId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_FETCH_ONE_SUCCESS, referralId: action.referralId, referral});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REFERRAL_FETCH_ONE_FAILURE, referralId: action.referralId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ReferralAction} action
 */
export function* saveReferral(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ REFERRAL_SAVE saga', action.referral?.id);
    
	if (action.referral === undefined || action.referral === null) {
		console.error('saveReferral received a null');
		throw new Error('saveReferral received a null');
	}

	if (!(action.referral instanceof Referral)) {
		action.referral = new Referral(action.referral);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'referral', action.referral);
		if (addResult.isErr()) {
			console.error(`⮂ saveReferral: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.REFERRAL_SAVE_FAILURE, referral: action.referral, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_SAVE, referral: action.referral});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.REFERRAL_SAVE_FAILURE, referral: action.referral, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ReferralAction} action
 * @param {Referral[]} action.referrals
 */
export function* saveManyReferrals(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ REFERRAL_SAVE_MANY saga', action.referrals.map(referral => referral.id).join(','));
    
    if (!Array.isArray(action.referrals)) {
        console.error('saveManyReferrals expected an array, received', action.referrals);
        throw new Error('saveManyReferrals expected an array');
    }

	try {
		const toSave: Referral[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.referrals.length; i++) {
			const referral = action.referrals[i];
			referral.persisted = false;
			toSave.push(referral);
		}

		const addResult = yield call(idb.add, 'referral', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyReferrals: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.REFERRAL_SAVE_MANY_FAILURE, referrals: action.referrals, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_SAVE_MANY, referrals: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.REFERRAL_SAVE_MANY_FAILURE, referrals: action.referrals, status: 500, err});

		console.error(`Error saving ${action.referrals.length} referrals to database`, err);
	}
}

/**
 * @param {ReferralAction} action
 */
export function* removeReferral(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.referral === undefined || action.referral === null || !(action.referral instanceof Referral)) {
			console.error('removeReferral expected type Referral, received', action.referral);
			throw new Error('removeReferral expected type Referral');
		}

	try {
		// Mark as deleted
		action.referral.status = 'D';
		action.referral.addedOn = newHRTimestamp();
		action.referral.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'referral', action.referral);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeReferral: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REFERRAL_REMOVE_FAILURE, referral: action.referral, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_REMOVE, referral: action.referral});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REFERRAL_REMOVE_FAILURE, referral: action.referral, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Referral.
	*
	* @param {ReferralAction} action
	*/
export function* restoreReferral(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreReferral expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.referral === undefined || action.referral === null) {
        console.error('restoreReferral expected a Referral, received null');
        throw new Error('restoreReferral expected a Referral, received null');
    }
    else if (!(action.referral instanceof Referral)) action.referral = new Referral(action.referral); 
    
	try {
		// Mark as restored
		action.referral.status = 'U';
		action.referral.addedOn = newHRTimestamp();
		action.referral.addedBy = userId;
		action.referral.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'referral', action.referral);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreReferral: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REFERRAL_RESTORE_FAILURE, referral: action.referral, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_RESTORE, referral: action.referral});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REFERRAL_RESTORE_FAILURE, referral: action.referral, status: 500, err});

		console.error('Error restoring from database', err);
	}
}